import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { today } from "@/lib/today";
import {
  LayoutDashboard,
  Building2,
  Home,
  AlertOctagon,
  CalendarX2,
  CalendarClock,
  CalendarRange,
  CalendarPlus,
  CalendarCheck,
  CalendarX,
  Mail,
  Flame,
  Users,
  Zap,
  PlugZap,
  Camera,
  FileSearch,
  FolderClock,
  Archive,
  PauseCircle,
} from "lucide-react";
import { useBiens } from "@/hooks/useBiens";
import { useProfiles } from "@/hooks/useProfiles";
import { cn } from "@/lib/utils";
import type { BienRow } from "@/schemas/bien";
import { getLifecycleStage, isCycleIncomplet } from "@/v2/lib/bienLifecycle";
import { countByBucket, getMailingCompleteness, MARKET_BUCKETS, type MarketBucket, isPebAbsent, isPebExpiring6m } from "@/v2/lib/dashboardBuckets";
import { matchView } from "@/lib/dashboardViews";

interface KpiCardProps {
  label: string;
  value: string | number;
  hint?: string;
  color?: "default" | "danger" | "warning" | "success" | "accent";
  onClick?: () => void;
  icon?: typeof Building2;
  /** Variante mise en avant — plus grosse police. */
  size?: "sm" | "lg";
}

function KpiCard({ label, value, hint, color = "default", onClick, icon: Icon, size = "sm" }: KpiCardProps) {
  // Carte toujours neutre (fond + bordure par défaut). Seul le chiffre
  // prend la couleur sémantique. Décision user 2026 : pas de teinte dans
  // les cards pour éviter le bruit visuel.
  const valueColor = {
    default: "text-text-primary",
    danger: "text-danger",
    warning: "text-warning",
    success: "text-success",
    accent: "text-accent",
  }[color];

  return (
    <button
      type="button"
      disabled={!onClick}
      onClick={onClick}
      className={cn(
        "text-left rounded-lg border border-border bg-surface p-4 transition-colors",
        onClick && "hover:border-accent cursor-pointer",
        !onClick && "cursor-default",
      )}
    >
      <div className="flex items-start justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider font-semibold text-text-muted">
          {label}
        </span>
        {Icon && <Icon size={16} className="text-text-muted" />}
      </div>
      <div className={cn("font-bold leading-none", valueColor, size === "lg" ? "text-4xl" : "text-3xl")}>
        {value}
      </div>
      {hint && <div className="text-xs text-text-muted mt-1.5">{hint}</div>}
    </button>
  );
}

const isLoue = (b: BienRow) => (b.disponibilite || "").toUpperCase() === "LOUÉ";

const BUCKET_ICONS: Record<MarketBucket, typeof Building2> = {
  "future": CalendarPlus,
  "lt-1m": CalendarClock,
  "1-3m": CalendarRange,
  "3-6m": CalendarRange,
  "gt-6m": CalendarX2,
};

/**
 * Couleur du bucket en fonction de la valeur :
 * - 0 → neutre (blanc) : pas d'alerte si la case est vide
 * - >0 → gradient vert (sain) → rouge (critique)
 *
 * Gradient demandé : future et <1m en vert, 1-3m en orange, 3-6m en
 * orange foncé/rouge. Le bucket gt-6m est sorti de la ligne 2 (déjà
 * présent comme tuile principale ligne 1 "Vide prolongé ≥ 6 mois").
 */
function bucketColor(bucket: MarketBucket, count: number): "default" | "danger" | "warning" | "success" | "accent" {
  if (count === 0) return "default";
  switch (bucket) {
    case "future": return "success";
    case "lt-1m": return "success";
    case "1-3m": return "warning";
    case "3-6m": return "danger";
    case "gt-6m": return "danger";
  }
}

export function CLevelInsightsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: biens, isLoading } = useBiens();
  const { data: profilesData } = useProfiles();

  // Collaborateurs actifs — source de la section "Par collaborateur"
  const collaborateurs = useMemo(() => {
    return (profilesData ?? [])
      .filter((p) => p.role === "collaborateur" && p.statut !== "inactif")
      .map((p) => ({
        id: p.id,
        name: `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || p.email,
      }));
  }, [profilesData]);

  // Compteurs ELS par collaborateur.
  // Progression (à gérer / date fixée / passées à clôturer / clôtures) :
  // DÉRIVÉE de getLifecycleStage — MÊME source que le Pipeline ELS — pour des
  // chiffres cohérents avec les colonnes affichées. On n'utilise plus le champ
  // brut `statutEls` (non synchronisé avec le workflow → comptages faux : tous
  // les biens actifs tombaient dans "à gérer", "date fixée" restait à 0).
  // Alertes transverses (PEB / transfert / photos) : dérivées de vrais champs,
  // conservées telles quelles via matchView.
  const elsByCollab = useMemo(() => {
    const list = biens ?? [];
    return collaborateurs.map((collab) => {
      const ctx = { collabId: collab.id };
      let avenirOk = 0, avenirIssue = 0, cloturePending = 0, finalClosurePending = 0;
      let pebAGerer = 0, transfertPasFait = 0, photosPasPret = 0;
      for (const b of list) {
        if (b.collaborateurSrs === collab.id) {
          switch (getLifecycleStage(b).stage) {
            case "ready_for_els": avenirIssue++; break;        // ELS à venir — à gérer
            case "a_lancer":      avenirIssue++; break;        // désigné, mission à lancer
            case "els_planned":   avenirOk++; break;           // ELS à venir — date fixée
            case "els_done":      cloturePending++; break;     // ELS passées à clôturer
            case "closing":       finalClosurePending++; break; // Clôtures à fermer
          }
        }
        if (matchView(b as never, "peb-a-gerer-collab", ctx)) pebAGerer++;
        if (matchView(b as never, "transfert-pas-fait-collab", ctx)) transfertPasFait++;
        if (matchView(b as never, "photos-pas-pret-collab", ctx)) photosPasPret++;
      }
      return { ...collab, avenirOk, avenirIssue, cloturePending, finalClosurePending, pebAGerer, transfertPasFait, photosPasPret };
    });
  }, [biens, collaborateurs]);

  const stats = useMemo(() => {
    const list = biens || [];
    const total = list.length;
    const loue = list.filter(isLoue).length;
    const vendu = list.filter((b) => (b.disponibilite || "").toUpperCase() === "VENDU").length;
    const buckets = countByBucket(list);
    // Total des biens DISPONIBLES qualifiés (= somme exacte des 5 buckets ligne 2)
    const dispoTotal = buckets.future + buckets["lt-1m"] + buckets["1-3m"] + buckets["3-6m"] + buckets["gt-6m"];

    // Identité à compléter : ref/projet/type/adresse/ville/étage/libre le
    // manquants → le bien reste chez le C-Level, invisible pour publication,
    // mailing et collab (plan « deux cycles », 2026-09-15 : les champs du
    // cycle ELS/PEB ne comptent plus ici).
    const preCollab = list.filter((b) => getLifecycleStage(b).stage === "draft_clevel").length;
    // Cycles à compléter : cycle ouvert où il manque quelqu'un — collaborateur
    // (bloquant) ou prestataire ELS / expert PEB (non bloquants). Même
    // prédicat que le chip catalogue.
    const cycleIncomplet = list.filter((b) => isCycleIncomplet(b)).length;

    // Pre-mailing : biens CLevel OK mais mailing incomplet → le bien est
    // au collab mais ne peut pas être publié (marketing bloqué).
    const preMailing = list.filter((b) => {
      const d = (b.disponibilite || "").toUpperCase();
      if (d === "LOUÉ" || d === "VENDU") return false;
      const ls = getLifecycleStage(b);
      if (ls.stage === "draft_clevel") return false;
      return !getMailingCompleteness(b).complete;
    }).length;

    // SRS administrative à clôturer : dossierCloture=true (collab a fait la
    // finalisation SRS) mais dossierClos=false (C-Level pas encore validé).
    // C'est l'action finale CEO sur le dossier locatif sortant.
    const srsAdminPending = list.filter((b) => b.dossierCloture === true && !b.dossierClos).length;

    // PEB absent / expirant — prédicats partagés avec le filtre catalogue
    // (isPebAbsent / isPebExpiring6m) → compteur tuile = liste filtrée.
    const pebAbsent = list.filter(isPebAbsent).length;
    const pebExpiring = list.filter(isPebExpiring6m).length;

    // Sous option (pause de vitrine réversible) : suivi C-Level. Compteur total
    // + combien sont parkés depuis > 7 jours (signal d'un bien oublié hors marché).
    // `today()` (helper) plutôt que Date.now() inline : source unique de la date
    // courante ET seul appel toléré en render par le lint react-compiler (l'appel
    // direct faisait échouer la CI « impure function during render »).
    const sousOptionList = list.filter((b) => !!(b as { sousOption?: boolean | null }).sousOption);
    const sousOptionCount = sousOptionList.length;
    const now = today().getTime();
    const sousOptionStale = sousOptionList.filter((b) => {
      const raw = (b as { sousOptionDepuis?: string | null }).sousOptionDepuis;
      const ts = raw ? new Date(raw).getTime() : NaN;
      return !Number.isNaN(ts) && now - ts > 7 * 24 * 3600 * 1000;
    }).length;

    // Taux d'occupation : loués / (loués + dispos qualifiés).
    const locatifs = loue + dispoTotal;
    const tauxOccupation = locatifs > 0 ? Math.round((loue * 100) / locatifs) : 0;

    return { total, loue, vendu, buckets, dispoTotal, preCollab, cycleIncomplet, preMailing, srsAdminPending, pebAbsent, pebExpiring, sousOptionCount, sousOptionStale, tauxOccupation };
  }, [biens]);

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-text-primary flex items-center gap-2">
          <LayoutDashboard size={22} className="text-accent" />
          {t("nav.dashboard")}
        </h1>
        <p className="text-sm text-text-muted mt-1">
          {t("v2.insights.subtitle")}
        </p>
      </div>

      {isLoading ? (
        <div className="text-center text-text-muted py-12">{t("common.loading")}</div>
      ) : (
        <>
          {/* Ligne 1 — Actions urgentes CEO (5 grosses tuiles). Les deux
              premières = les deux garde-fous distincts du plan « deux
              cycles » (2026-09-15) : identité (bloque tout) et cycle de
              sortie (ne bloque que pipeline/missions). */}
          <section className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
            <KpiCard
              label={t("v2.insights.preCollab.label")}
              value={stats.preCollab}
              hint={t("v2.insights.preCollab.hint")}
              color={stats.preCollab > 0 ? "danger" : "success"}
              onClick={() => navigate("/v2/biens?filter=blocked")}
              icon={AlertOctagon}
              size="lg"
            />
            <KpiCard
              label={t("v2.insights.cycleIncomplet.label")}
              value={stats.cycleIncomplet}
              hint={t("v2.insights.cycleIncomplet.hint")}
              color={stats.cycleIncomplet > 0 ? "warning" : "success"}
              onClick={() => navigate("/v2/biens?filter=cycle-incomplet")}
              icon={AlertOctagon}
              size="lg"
            />
            <KpiCard
              label={t("v2.insights.preMailing.label")}
              value={stats.preMailing}
              hint={t("v2.insights.preMailing.hint")}
              color={stats.preMailing > 0 ? "warning" : "success"}
              onClick={() => navigate("/v2/biens?filter=incomplete-mailing")}
              icon={Mail}
              size="lg"
            />
            <KpiCard
              label={t("v2.insights.srsAdmin.label")}
              value={stats.srsAdminPending}
              hint={t("v2.insights.srsAdmin.hint")}
              color={stats.srsAdminPending > 0 ? "warning" : "success"}
              onClick={() => navigate("/v2/pipeline?focus=srs-admin")}
              icon={Archive}
              size="lg"
            />
            <KpiCard
              label={t("v2.insights.dispoTotal.label")}
              value={stats.dispoTotal}
              hint={t("v2.insights.dispoTotal.hint")}
              color={stats.dispoTotal > 0 ? "accent" : "default"}
              onClick={() => navigate("/v2/apparts-for-rent")}
              icon={Building2}
              size="lg"
            />
          </section>

          {/* Ligne 2 — Disponibilités OK mailing (5 buckets, gradient vert→rouge) */}
          <section className="mb-5">
            <h2 className="text-xs uppercase tracking-wider text-text-muted font-semibold mb-2">
              {t("v2.insights.sections.dispo")}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {MARKET_BUCKETS.map((m) => (
                <KpiCard
                  key={m.key}
                  label={t(m.labelKey)}
                  value={stats.buckets[m.key]}
                  hint={t(m.hintKey)}
                  color={bucketColor(m.key, stats.buckets[m.key])}
                  onClick={() => navigate(`/v2/biens?filter=bucket-${m.key}`)}
                  icon={BUCKET_ICONS[m.key]}
                />
              ))}
            </div>
          </section>

          {/* Ligne 3 — Catalogue & qualité (5 vignettes) */}
          <section className="mb-5">
            <h2 className="text-xs uppercase tracking-wider text-text-muted font-semibold mb-2">
              {t("v2.insights.sections.catalogue")}
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              <KpiCard
                label={t("v2.insights.sousOption.label")}
                value={stats.sousOptionCount}
                hint={t("v2.insights.sousOption.hint")}
                color={stats.sousOptionStale > 0 ? "warning" : stats.sousOptionCount > 0 ? "accent" : "default"}
                icon={PauseCircle}
                onClick={() => navigate("/v2/biens?filter=sous-option")}
              />
              <KpiCard
                label={t("v2.insights.pebAbsent.label")}
                value={stats.pebAbsent}
                hint={t("v2.insights.pebAbsent.hint")}
                color={stats.pebAbsent > 0 ? "warning" : "success"}
                icon={Flame}
                onClick={() => navigate("/v2/biens?filter=peb-absent")}
              />
              <KpiCard
                label={t("v2.insights.pebExpiring.label")}
                value={stats.pebExpiring}
                hint={t("v2.insights.pebExpiring.hint")}
                color={stats.pebExpiring > 0 ? "warning" : "success"}
                icon={Flame}
                onClick={() => navigate("/v2/biens?filter=peb-expirant")}
              />
              <KpiCard
                label={t("v2.insights.loue.label")}
                value={stats.loue}
                hint={t("v2.insights.loue.hint", { taux: stats.tauxOccupation })}
                color="success"
                onClick={() => navigate("/v2/biens?filter=loue")}
                icon={Home}
              />
              <KpiCard
                label={t("v2.insights.total.label")}
                value={stats.total}
                hint={t("v2.insights.total.hint")}
                onClick={() => navigate("/v2/biens")}
                icon={Building2}
              />
            </div>
          </section>

          {/* Ligne 4 — ELS par collaborateur (1 card par collab actif) */}
          <section>
            <h2 className="text-xs uppercase tracking-wider text-text-muted font-semibold mb-2 flex items-center gap-1.5">
              <Users size={12} className="text-text-muted" /> {t("v2.insights.sections.els")}
            </h2>
            {elsByCollab.length === 0 ? (
              <div className="bg-surface rounded-md border border-border p-6 text-center text-sm text-text-muted">
                {t("dashboard.els.noCollaborateur")}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {elsByCollab.map((c) => (
                  <div key={c.id} className="bg-surface border border-border rounded-lg p-4">
                    <div className="text-sm font-semibold text-text-primary mb-3">
                      {c.name}
                    </div>
                    <div className="flex flex-col gap-1 text-sm">
                      {/* PEB à gérer en 1re ligne (décision user 2026-06-02) :
                          c'est tous les PEB à faire, prioritaire avant l'ELS. */}
                      <CollabRow
                        icon={Zap}
                        label={t("v2.insights.collab.pebAGerer")}
                        value={c.pebAGerer}
                        tone={c.pebAGerer > 0 ? "warning" : "muted"}
                        to={`/v2/pipeline?collab=${c.id}&highlight=peb`}
                      />
                      <CollabRow
                        icon={CalendarCheck}
                        label={t("v2.insights.collab.elsDateFixee")}
                        value={c.avenirOk}
                        tone={c.avenirOk > 0 ? "success" : "muted"}
                        to={`/v2/pipeline?collab=${c.id}&focus=els-fixee`}
                      />
                      <CollabRow
                        icon={CalendarX}
                        label={t("v2.insights.collab.elsAGerer")}
                        value={c.avenirIssue}
                        tone={c.avenirIssue > 0 ? "warning" : "muted"}
                        to={`/v2/pipeline?collab=${c.id}&focus=ready-for-els`}
                      />
                      <CollabRow
                        icon={FileSearch}
                        label={t("v2.insights.collab.elsACloturer")}
                        value={c.cloturePending}
                        tone={c.cloturePending > 0 ? "danger" : "muted"}
                        to={`/v2/pipeline?collab=${c.id}&focus=els-fait`}
                      />
                      <CollabRow
                        icon={FolderClock}
                        label={t("v2.insights.collab.cloturesAFermer")}
                        value={c.finalClosurePending}
                        tone={c.finalClosurePending > 0 ? "warning" : "muted"}
                        to={`/v2/pipeline?collab=${c.id}&focus=srs-admin`}
                      />
                      <CollabRow
                        icon={PlugZap}
                        label={t("v2.insights.collab.transferts")}
                        value={c.transfertPasFait}
                        tone={c.transfertPasFait > 0 ? "warning" : "muted"}
                        to={`/v2/pipeline?collab=${c.id}&highlight=transfert`}
                      />
                      <CollabRow
                        icon={Camera}
                        label={t("v2.insights.collab.photos")}
                        value={c.photosPasPret}
                        tone={c.photosPasPret > 0 ? "warning" : "muted"}
                        to={`/v2/pipeline?collab=${c.id}&highlight=photos`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

// ─── Sub-component pour les lignes du mini-dashboard collab ────────────────

interface CollabRowProps {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  value: number;
  tone: "success" | "warning" | "danger" | "muted";
  to: string;
}

function CollabRow({ icon: Icon, label, value, tone, to }: CollabRowProps) {
  const toneCls = {
    success: "text-success",
    warning: "text-warning",
    danger: "text-danger",
    muted: "text-text-muted",
  }[tone];
  return (
    <Link
      to={to}
      className="flex items-center justify-between -mx-2 px-2 py-1 rounded hover:bg-gray-50 transition-colors cursor-pointer outline-none"
    >
      <span className="inline-flex items-center gap-2 text-text-secondary">
        <Icon size={13} className="text-text-muted" />
        {label}
      </span>
      <span className={cn("text-sm font-semibold tabular-nums", toneCls)}>{value}</span>
    </Link>
  );
}
